import { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  usePublicFormsControllerShareView,
  usePublicFormsControllerShareRespond,
} from '@/apis/backend/generated/public-forms/public-forms';
import { useFormPreview } from '@/components/signup-forms/use-form-preview';

import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Stack,
  Alert,
  Paper,
  Button,
  Dialog,
  Container,
  TextField,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';

export const Route = createFileRoute('/f/$token')({
  component: FormReviewPage,
});

/**
 * The white-glove review page: a merchant opens the possession-token link,
 * the live popup plays over this page (real storefront runtime in test mode —
 * entrance animation, games and all; nothing is recorded), and they approve or
 * request changes. No account, no dashboard.
 */
function FormReviewPage() {
  const { token } = Route.useParams();
  const { t } = useTranslation('signupForms');
  const queryClient = useQueryClient();
  const { openTest } = useFormPreview();

  const { data, error, isLoading } = usePublicFormsControllerShareView(token, {
    query: { enabled: !!token, retry: false },
  });

  const respondMutation = usePublicFormsControllerShareRespond({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({
          predicate: (q) =>
            typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).includes('/public/forms/share'),
        }),
    },
  });

  // The share payload is fully brand-resolved server-side, so the runtime gets
  // it verbatim (identity resolver) — what plays here is what ships.
  const play = useCallback(() => {
    if (!data) return false;
    return openTest({ id: 'share-review', html: data.html, config: data.config }, (h) => h ?? '');
  }, [data, openTest]);

  // Auto-play once the payload and the runtime script are both ready; the
  // runtime loads async so poll briefly instead of racing it.
  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current || !data) return;
    const tick = setInterval(() => {
      if (play()) {
        openedRef.current = true;
        clearInterval(tick);
      }
    }, 250);
    const stop = setTimeout(() => clearInterval(tick), 15000);
    return () => {
      clearInterval(tick);
      clearTimeout(stop);
    };
  }, [data, play]);

  const [changesOpen, setChangesOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [name, setName] = useState('');
  const [changeResponse, setChangeResponse] = useState(false);

  const respond = (action: 'approve' | 'request_changes') => {
    respondMutation.mutate({
      token,
      data: {
        action,
        ...(action === 'request_changes' && feedback.trim() ? { feedback: feedback.trim() } : {}),
        ...(name.trim() ? { name: name.trim() } : {}),
      },
    });
    setChangesOpen(false);
    setChangeResponse(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Alert severity="warning">
          {t('signupForms.review.gone', 'This review link is no longer active. Ask your contact for a fresh one.')}
        </Alert>
      </Box>
    );
  }

  const responded = data.status !== 'pending' && !changeResponse;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 } }}>
          <Stack spacing={3} alignItems="center" textAlign="center">
            {data.brandName && (
              <Typography variant="overline" color="text.secondary">
                {data.brandName}
              </Typography>
            )}
            <Typography variant="h4" component="h1">
              {t('signupForms.review.title', 'Review your new signup popup')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t(
                'signupForms.review.subtitle',
                'It just played over this page — exactly as visitors will see it on your site. Play it as many times as you like.',
              )}
            </Typography>

            <Button variant="outlined" startIcon={<ReplayIcon />} onClick={play}>
              {t('signupForms.review.replay', 'Play it again')}
            </Button>

            {responded ? (
              <Stack spacing={2} alignItems="center">
                {data.status === 'approved' ? (
                  <Alert icon={<CheckCircleIcon />} severity="success">
                    {t('signupForms.review.approvedState', "Approved — thanks! We'll take it from here.")}
                  </Alert>
                ) : (
                  <Alert severity="info">
                    {t(
                      'signupForms.review.changesState',
                      "Thanks — your feedback is with the team. You'll get an updated version to review on this same link.",
                    )}
                  </Alert>
                )}
                <Button size="small" color="inherit" onClick={() => setChangeResponse(true)}>
                  {t('signupForms.review.changeResponse', 'Change my response')}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={2} sx={{ width: '100%', maxWidth: 360 }}>
                <TextField
                  size="small"
                  label={t('signupForms.review.name', 'Your name (optional)')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    disabled={respondMutation.isPending}
                    onClick={() => respond('approve')}
                  >
                    {t('signupForms.review.approve', 'Approve')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    disabled={respondMutation.isPending}
                    onClick={() => setChangesOpen(true)}
                  >
                    {t('signupForms.review.requestChanges', 'Request changes')}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Paper>
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 3 }}>
          {t('signupForms.review.poweredBy', 'Designed and delivered with nexmail')}
        </Typography>
      </Container>

      <Dialog open={changesOpen} onClose={() => setChangesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('signupForms.review.changesTitle', 'What should we change?')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            sx={{ mt: 1 }}
            placeholder={t(
              'signupForms.review.changesPlaceholder',
              'e.g. Use our summer campaign photo, and make the discount 15% instead of 10%.',
            )}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangesOpen(false)}>{t('signupForms.review.cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            disabled={!feedback.trim() || respondMutation.isPending}
            onClick={() => respond('request_changes')}
          >
            {t('signupForms.review.sendFeedback', 'Send feedback')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
